import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Flag, X, Warning } from "@phosphor-icons/react";
import { adminApi } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PassportLogo } from "@/components/Reveal";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const CATS = {
    spam: "Spam", harassment: "Harassment", hate: "Hate", sexual: "Sexual",
    violence: "Violence", scam: "Scam", copyright: "Copyright", other: "Other",
};

const ACTIONS = [
    { k: "dismiss", l: "Dismiss" },
    { k: "warn_user", l: "Warn user" },
    { k: "remove_content", l: "Remove content" },
    { k: "ban_user", l: "Ban user" },
];

export default function AdminModeration() {
    const { user, loading } = useAuth();
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [reports, setReports] = useState([]);
    const [filter, setFilter] = useState("open");

    const load = useCallback(async () => {
        try {
            const s = await adminApi.stats();
            setStats(s);
            const r = await adminApi.reports(filter);
            setReports(r);
        } catch (e) {
            // Not admin
        }
    }, [filter]);

    useEffect(() => { if (user?.is_admin || user?.tier === "elite") load(); /* attempt on any elite */ }, [user, load]);

    if (loading) return <div className="min-h-screen grid place-items-center text-white/60">{t("common.loading")}</div>;
    if (!user) return <Navigate to="/auth" />;

    const resolve = async (id, action) => {
        const note = action === "warn_user" || action === "ban_user" ? window.prompt(t("admin.addNote")) : "";
        try {
            await adminApi.resolve(id, action, note || "");
            toast.success(t("admin.resolved"));
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("admin.notAuthorized"));
        }
    };

    return (
        <div className="min-h-screen grain" data-testid="admin-moderation">
            <header className="sticky top-0 z-40 glass border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                    <Link to="/app" className="flex items-center gap-3">
                        <PassportLogo size={32} />
                        <div className="hidden sm:block">
                            <div className="font-serif text-lg text-white">Passport</div>
                            <div className="overline" style={{ fontSize: "8px" }}>{t("admin.moderation")}</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-2">
                        <ShieldCheck size={14} color="#D4AF37" />
                        <span className="text-xs text-white/60">{t("admin.adminPanel")}</span>
                    </div>
                </div>
            </header>

            <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-12">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
                    <div className="overline mb-2">/ {t("admin.moderation")}</div>
                    <h1 className="font-serif text-5xl md:text-6xl text-white tracking-tighter">{t("admin.communityIntegrity")}</h1>
                </motion.div>

                {stats ? (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-10">
                        {[
                            [t("admin.users"), stats.users],
                            [t("admin.posts"), stats.posts],
                            [t("admin.comments"), stats.comments],
                            [t("admin.reportsOpen"), stats.reports_open],
                            [t("admin.reportsDone"), stats.reports_resolved],
                            [t("admin.media"), stats.media_active],
                        ].map(([l, v]) => (
                            <div key={l} className="rounded-2xl p-4" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                                <div className="font-serif text-3xl text-white">{v}</div>
                                <div className="text-[10px] tracking-widest uppercase text-white/50 mt-1">{l}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-white/50 text-sm mb-10">{t("admin.needAdmin")}</div>
                )}

                <div className="flex items-center gap-2 mb-4">
                    <div className="flex gap-1 p-1 rounded-full" style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)" }}>
                        {[
                            { k: "open", l: t("admin.open") },
                            { k: "resolved", l: t("admin.resolved") },
                            { k: "", l: t("admin.all") },
                        ].map((m) => (
                            <button key={m.l} onClick={() => setFilter(m.k)} className="px-3 py-1.5 rounded-full text-xs" style={{ background: filter === m.k ? "var(--gold)" : "transparent", color: filter === m.k ? "var(--navy)" : "rgba(255,255,255,0.8)" }} data-testid={`admin-filter-${m.k || "all"}`}>
                                {m.l}
                            </button>
                        ))}
                    </div>
                    <button onClick={load} className="btn-ghost text-xs !py-1.5 !px-3 ml-auto">{t("admin.refresh")}</button>
                </div>

                <div className="space-y-3">
                    {reports.length === 0 ? (
                        <div className="text-center py-10 text-white/50 text-sm">{t("admin.noReports")}</div>
                    ) : reports.map((r) => (
                        <motion.div key={r.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl p-5" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={`report-${r.id}`}>
                            <div className="flex items-start gap-3">
                                <Flag size={18} color="#D4AF37" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 text-xs text-white/60">
                                        <span className="px-2 py-0.5 rounded-full text-[10px]" style={{ background: "rgba(212,175,55,0.14)", color: "var(--gold)" }}>{r.target_type}</span>
                                        <span className="px-2 py-0.5 rounded-full text-[10px]" style={{ background: "rgba(255,255,255,0.06)" }}>{t(`admin.cats.${r.category}`) || r.category}</span>
                                        <span>{t("admin.reportedBy")} <span style={{ color: "var(--gold)" }}>@{r.reporter_handle}</span></span>
                                        <span>{new Date(r.created_at).toLocaleString()}</span>
                                        <span className="ml-auto text-white/50">{r.status}</span>
                                    </div>
                                    {r.detail && <div className="mt-2 text-sm text-white/80">{r.detail}</div>}
                                    {r.target_snapshot && (
                                        <div className="mt-3 p-3 rounded-xl text-xs text-white/70" style={{ background: "rgba(0,0,0,0.25)" }}>
                                            {r.target_type === "post" && (
                                                <>
                                                    <div><span className="text-white/50">{t("admin.caption")}</span> {r.target_snapshot.caption || "(none)"}</div>
                                                    <div><span className="text-white/50">{t("admin.author")}</span> @{r.target_snapshot.user_handle}</div>
                                                </>
                                            )}
                                            {r.target_type === "comment" && (
                                                <>
                                                    <div><span className="text-white/50">{t("admin.text")}</span> {r.target_snapshot.text}</div>
                                                    <div><span className="text-white/50">{t("admin.author")}</span> @{r.target_snapshot.user_handle}</div>
                                                </>
                                            )}
                                            {r.target_type === "user" && (
                                                <div><span className="text-white/50">{t("admin.user")}</span> @{r.target_snapshot.handle} ({r.target_snapshot.name})</div>
                                            )}
                                        </div>
                                    )}
                                    {r.status === "open" && (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {ACTIONS.map((a) => (
                                                <button key={a.k} onClick={() => resolve(r.id, a.k)} className="text-xs px-3 py-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.06)", color: "white" }} data-testid={`admin-action-${r.id}-${a.k}`}>
                                                    {t(`admin.actions.${a.k}`)}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {r.status === "resolved" && (
                                        <div className="mt-2 text-xs text-white/50">{t("admin.action")} <span style={{ color: "var(--gold)" }}>{r.action}</span>{r.resolved_note ? ` — ${r.resolved_note}` : ""}</div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
}
